import { ReactNode, useRef, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Box } from '../UI/Box'
import { useSelector } from 'react-redux'
import { selectBottomNavButton } from '../../modules/miscUi/selectors'
import { overlayPageCss, useShouldShowForThreshold } from '../../theme'
import { ButtonNavActionButton } from '../BottomMenu'

export default function FullScreenOverlay({
  children,
  showHeader = false,
  hideBottomMenu = false,
  dismissable = false,
  disableScroll = false,
  noBottomPadding = false,
}: {
  children: ReactNode | ReactNode[]
  showHeader?: boolean
  hideBottomMenu?: boolean
  dismissable?: boolean
  disableScroll?: boolean
  noBottomPadding?: boolean
}) {
  const bottomNavButton = useSelector(selectBottomNavButton)
  const isAtLeastMediumScreen = useShouldShowForThreshold('medium')
  const hasBottomButton = !!bottomNavButton && isAtLeastMediumScreen

  const windowHeight = window.innerHeight
  const innerRef = useRef<HTMLDivElement>(null)

  const [scrollAmountRemaining, setScrollAmountRemaining] = useState(0)

  useEffect(() => {
    if (!innerRef.current) return

    const listenToScroll = () => {
      const amountScrolled = innerRef.current?.scrollTop
      const scrollAmountRemaining =
        innerRef.current?.scrollHeight -
        innerRef.current?.clientHeight -
        amountScrolled
      setScrollAmountRemaining(
        scrollAmountRemaining < 32 ? 0 : Math.floor(scrollAmountRemaining),
      )
    }

    listenToScroll()

    innerRef.current.addEventListener('scroll', listenToScroll)

    return () => {
      innerRef.current?.removeEventListener('scroll', listenToScroll)
    }
  }, [innerRef.current, windowHeight, children])

  return (
    <Wrapper
      showHeader={showHeader || dismissable || isAtLeastMediumScreen}
      hideBottomMenu={hideBottomMenu}
      hasBottomButton={hasBottomButton}
      disableScroll={disableScroll}
    >
      <Inner
        id="full-screen-overlay"
        dc
        showHeader={showHeader || dismissable || isAtLeastMediumScreen}
        hideBottomMenu={hideBottomMenu}
        dismissable={dismissable}
        hasBottomButton={hasBottomButton}
        disableScroll={disableScroll}
        noBottomPadding={noBottomPadding}
        ref={innerRef}
      >
        {children}
        {!!hasBottomButton && (
          <ButtonNavActionButton
            modalHeight={innerRef.current?.clientHeight ?? 0}
            scrollAmountRemaining={scrollAmountRemaining}
            buttonType={bottomNavButton}
            modalRef={innerRef}
          />
        )}
      </Inner>
    </Wrapper>
  )
}

type SizeProps = {
  showHeader: boolean
  hideBottomMenu: boolean
  hasBottomButton: boolean
  dismissable?: boolean
  disableScroll?: boolean
  noBottomPadding?: boolean
}

function getBottomPadding(showHeader: boolean, hasBottomButton: boolean) {
  console.log({ showHeader, hasBottomButton })
  return hasBottomButton
    ? showHeader
      ? 80 + 69 * 2
      : 69 * 2
    : showHeader
      ? 80 + 69 * 2
      : 69
}

const disableScrollCss = css<SizeProps>`
  overflow-y: ${({ disableScroll }) => (disableScroll ? 'hidden' : 'auto')};
`

const Wrapper = styled(Box)<SizeProps>`
  position: fixed;
  top: ${({ showHeader }) => (showHeader ? 80 : 0)}px;
  left: 0;
  z-index: 42068;
  background: var(--surface-primary);
  height: 100vh;
  ${disableScrollCss}
  padding-bottom: ${({ hasBottomButton, showHeader }) =>
    getBottomPadding(showHeader, hasBottomButton)}px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    padding-top: var(--Index-Grids-Gutters);
    padding-bottom: ${({ hasBottomButton, showHeader, dismissable }) =>
      dismissable && hasBottomButton
        ? '0px'
        : getBottomPadding(showHeader, hasBottomButton)}px;
    overflow-y: hidden;
    justify-content: center;
  }
`

const Inner = styled(Box)<SizeProps>`
  ${overlayPageCss}
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    position: relative;
    padding-top: ${({ dismissable }) =>
      dismissable ? 0 : 'var(--Index-Grids-Gutters)'};
    padding-bottom: ${({ hasBottomButton, noBottomPadding }) =>
      noBottomPadding
        ? '24px'
        : hasBottomButton
          ? '0px'
          : 'var(--Spacing - sectionPadding)'};
    padding-left: var(--Index-Grids-Gutters);
    padding-right: var(--Index-Grids-Gutters);
    border-radius: var(--units-12, 12px);
    border: 1px solid var(--stroke-secondary, #333);
    background: var(--surface-primary, #080808);
    ${disableScrollCss}
    max-height: calc(100vh - ${({ hasBottomButton, showHeader }) =>
      getBottomPadding(showHeader, hasBottomButton)}px + 32px);
  }
`
